/* =====================================================================
   08 Customer reviews — hydration for ns--section-homepage-s8.liquid

   The section is already readable when this runs: Liquid server-rendered the aggregate, the
   photo strip and the newest cards out of the `reviews_stats` metaobject. This script takes
   over filtering, sorting, pagination and the photo gallery.

   Loading, in order of cost:
     index-1  (7KB, newest 100)  — fetched immediately, replaces the server-rendered cards.
     index-2  (238KB, the rest)  — fetched ONLY when it is actually needed: the visitor
                                   filters/sorts, opens the gallery, or the section is
                                   uncapped (the /pages/reviews archive).
     photos   (5KB, all 246)     — fetched when the gallery opens, so the grid paints without
                                   waiting on index-2.

   Filtering is client-side by necessity, not preference: the Storefront API's `metaobjects`
   query takes no filter argument and sorts only by id/updated_at, so a query like
   "5 stars + tag, newest first" cannot be asked of the store.

   A review's position in the combined [index-1, index-2] array is its identity — that is the
   `k` in reviews-photos.json. `pool` therefore must never be reordered in place.
   ===================================================================== */
(() => {
  const INIT = 6;
  const STEP = 6;
  const LONG_BODY = 320; // chars past which a card clamps and offers "Read more"

  const ARROW = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M6 13l6 6 6-6"/></svg>';
  const ARROW_R = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
  // Small external-link glyph shown on a review's source pill when it links out to the original.
  const EXTARROW = '<svg class="rv-src-ext" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 17 17 7"/><path d="M8 7h9v9"/></svg>';

  const ENT = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  // Review text is visitor-written. Even the benign stuff bites: bodies contain "<3".
  const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ENT[c]);
  const cdn = (u, w) => u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  // A review's source URL is only safe to link if it parses to a real http(s) URL — reject
  // javascript:/data:/garbage and degenerate values like "https://" (no host).
  const safeUrl = (u) => {
    if (!u) return null;
    try { const p = new URL(u, location.href); return (p.protocol === 'http:' || p.protocol === 'https:') && p.hostname ? p.href : null; }
    catch (e) { return null; }
  };

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // One in-flight promise per URL, shared by every section on the page.
  const jsonCache = {};
  const getJSON = (url) => {
    if (!jsonCache[url]) {
      jsonCache[url] = fetch(url).then((r) => {
        if (!r.ok) throw new Error(url + ' -> ' + r.status);
        return r.json();
      });
    }
    return jsonCache[url];
  };

  const DAY = 86400000;
  // Mirrors the Liquid in the section — keep the two in step or cards shift on hydration.
  function ago(d) {
    const t = Date.parse(d + 'T00:00:00Z');
    if (!t) return '';
    const days = Math.floor((Date.now() - t) / DAY);
    if (days < 1) return 'today';
    if (days < 7) return days + ' days ago';
    if (days < 35) return Math.floor(days / 7) + 'w ago';
    if (days < 365) return Math.floor(days / 30) + 'mo ago';
    return Math.floor(days / 365) + 'y ago';
  }

  const stars = (n) => {
    let out = '';
    for (let i = 1; i <= 5; i++) out += '<i' + (i <= n ? '' : ' class="off"') + '>&#9733;</i>';
    return out;
  };

  function card(r) {
    const name = r.n || 'Anonymous';
    const initial = name.trim().charAt(0).toUpperCase() || '?';
    const body = r.b || '';
    const long = body.length > LONG_BODY;
    const shots = (r.im || [])
      .map((u) => '<button type="button" data-rv-shot="' + esc(u) + '" aria-label="Open photo">' +
        '<img src="' + esc(cdn(u, 120)) + '" alt="" loading="lazy" decoding="async"></button>')
      .join('');
    return '<article class="rv-card">' +
      '<div class="rv-id"><div class="rv-av" aria-hidden="true">' + esc(initial) + '</div><div>' +
        '<div class="nm">' + esc(name) + '</div>' +
        (r.v ? '<div class="vf">Verified purchase</div>' : '') +
      '</div></div>' +
      '<div class="rv-meta"><span class="st" role="img" aria-label="' + esc(r.r) + ' out of 5">' +
        stars(r.r) + '</span><span class="ago">' + esc(ago(r.d)) + '</span></div>' +
      (r.ti ? '<p class="rv-ttl">' + esc(r.ti) + '</p>' : '') +
      '<p class="rv-quote' + (long ? ' clamp' : '') + '">&ldquo;' + esc(body) + '&rdquo;</p>' +
      (long ? '<button class="rv-readmore" type="button" data-rv-readmore>Read more</button>' : '') +
      (shots ? '<div class="rv-shots">' + shots + '</div>' : '') +
      '<div class="rv-foot">' + (safeUrl(r.u)
        ? '<a class="rv-src rv-src-link" href="' + esc(safeUrl(r.u)) + '" target="_blank" rel="noopener nofollow" title="Read this review on ' + esc(r.src || 'the source') + '">' + esc(r.src || '') + EXTARROW + '</a>'
        : '<span class="rv-src">' + esc(r.src || '') + '</span>') + '</div>' +
    '</article>';
  }

  function init(root) {
    const grid = root.querySelector('[data-rv-grid]');
    if (!grid) return;
    const reveal = root.querySelector('[data-rv-reveal]');
    const moreBtn = root.querySelector('[data-rv-more]');
    const filtersEl = root.querySelector('[data-rv-filters]');
    const sortEl = root.querySelector('[data-rv-sort]');
    const dist = root.querySelector('[data-rv-dist]');

    const cap = parseInt(root.dataset.rvCap, 10) || 0; // 0 = the whole archive
    const total = parseInt(root.dataset.rvTotal, 10) || 0;
    const totalFmt = root.dataset.rvTotalFmt || String(total);
    const allUrl = root.dataset.rvAllUrl || '';
    const url1 = root.dataset.rvIndex1;
    const url2 = root.dataset.rvIndex2;
    const urlPh = root.dataset.rvPhotos;

    let pool = [];        // always [idx1 rows, ...idx2 rows] — order IS identity, never sort it
    let idx1 = null, idx2 = null;  // raw index rows; pool is reassembled from these in fixed order
    let deep = false;     // index-2 merged in?
    let open = false;     // has the grid been expanded past its first clamp?
    let shown = INIT;
    let cacheList = null;
    let hydratePromise = null, deepPromise = null;
    let growSeq = 0;      // overlapping grow() calls: only the newest may finalize maxHeight
    // Rebuild pool from whatever index parts have arrived, always idx1 first so photo `k` stays stable.
    function rebuild() { pool = (idx1 || []).concat(idx2 || []); cacheList = null; }

    // The default view is whatever Liquid marked up — pressed chips and the selected option.
    // Reading it from the DOM keeps the default in one place and guarantees the server-rendered
    // cards match what this script paints first. Note a default filter is NOT an interaction:
    // index-2 stays unfetched until the visitor actually asks something of the archive.
    let sort = (sortEl && sortEl.value) || 'recent';
    const active = new Set();
    if (filtersEl) {
      filtersEl.querySelectorAll('.rv-chip[aria-pressed="true"]').forEach((c) => active.add(c.dataset.filter));
    }

    let photos = null;
    let lbAt = -1;

    const clampPx = () => Math.round(Math.max(380, Math.min(700, window.innerHeight * 0.56)));

    // One dialog per section, with its own state. A page-wide singleton would double-bind its
    // listeners and cross its `photos`/`lbAt` the moment a page carries two review sections.
    let lbEl = null;
    function lightbox() {
      if (lbEl) return lbEl;
      lbEl = document.createElement('dialog');
      lbEl.className = 'rv-lb';
      lbEl.innerHTML =
        '<div class="rv-lb-bar"><span data-lb-title></span>' +
        '<button class="rv-lb-x" type="button" aria-label="Close">&times;</button></div>' +
        '<div class="rv-lb-scroll"><div data-lb-body></div></div>';
      lbEl.querySelector('.rv-lb-x').addEventListener('click', () => lbEl.close());
      lbEl.addEventListener('click', (e) => { if (e.target === lbEl) lbEl.close(); });
      lbEl.addEventListener('click', (e) => {
        const one = e.target.closest('[data-lb-i]');
        if (one && photos) { lbAt = +one.dataset.lbI; renderLbOne(); return; }
        if (e.target.closest('[data-lb-all]')) { renderLbGrid(); return; }
        if (e.target.closest('[data-lb-prev]')) { if (lbAt > 0) { lbAt--; renderLbOne(); } return; }
        if (e.target.closest('[data-lb-next]')) { if (photos && lbAt < photos.length - 1) { lbAt++; renderLbOne(); } }
      });
      document.addEventListener('keydown', (e) => {
        if (!lbEl.open || lbAt < 0 || !photos) return;
        if (e.key === 'ArrowLeft' && lbAt > 0) { lbAt--; renderLbOne(); }
        if (e.key === 'ArrowRight' && lbAt < photos.length - 1) { lbAt++; renderLbOne(); }
      });
      document.body.appendChild(lbEl);
      return lbEl;
    }

    // index-1 (newest 100) — fetched once, guarded by a shared promise so interactions never race it.
    // Always resolves (errors are swallowed + the cache entry evicted so a later call can retry).
    function hydrate() {
      if (!hydratePromise) {
        hydratePromise = getJSON(url1).then(function (rows) { idx1 = rows; rebuild(); render(); })
          .catch(function (e) { hydratePromise = null; delete jsonCache[url1]; console.warn('[ns-reviews]', e); });
      }
      return hydratePromise;
    }
    // index-2 (the rest) — always awaits index-1 first so pool assembles in order. If index-1 failed
    // (idx1 still null) we do NOT load index-2 alone, or photo `k -> pool[k]` identity would be wrong.
    // A single .catch covers fetch rejection AND any throw in the success handlers; guard evicted on
    // failure so a later interaction can retry rather than break.
    function ensureDeep() {
      if (deep || !url2) return Promise.resolve();
      if (!deepPromise) {
        deepPromise = hydrate().then(function () {
          if (!idx1) return;
          root.setAttribute('data-rv-busy', '');
          return getJSON(url2).then(function (rows) { idx2 = rows; deep = true; rebuild(); render(); });
        }).catch(function (e) { deepPromise = null; delete jsonCache[url2]; console.warn('[ns-reviews]', e); })
          .then(function () { root.removeAttribute('data-rv-busy'); });
      }
      return deepPromise;
    }

    function pass(r) {
      for (const f of active) {
        if (f === '__verified') { if (!r.v) return false; }
        else if (f === '__photos') { if (!r.im || !r.im.length) return false; }
        else if (!r.t || r.t.indexOf(f) === -1) return false;
      }
      return true;
    }

    function cmp(a, b) {
      if (sort === 'high') return (b.r - a.r) || b.d.localeCompare(a.d);
      if (sort === 'low') return (a.r - b.r) || b.d.localeCompare(a.d);
      return b.d.localeCompare(a.d);
    }

    // filter() copies, so pool's own order survives — see the header note about `k`.
    function list() {
      if (!cacheList) cacheList = pool.filter(pass).sort(cmp);
      return cacheList;
    }
    const capped = () => (cap ? list().slice(0, cap) : list());

    function setMore() {
      const c = capped();
      const remaining = c.length - shown;
      moreBtn.hidden = false;
      moreBtn.classList.remove('done');
      delete moreBtn.dataset.rvLink;

      if (!open) {
        moreBtn.innerHTML = '<span class="lbl">Show more reviews</span>' + ARROW;
      } else if (remaining > 0) {
        moreBtn.innerHTML = '<span class="lbl">Show more reviews</span> <span class="ct">+' +
          Math.min(STEP, remaining) + '</span>' + ARROW;
      } else if (cap && allUrl && total > c.length) {
        // This instance is a window onto the archive; hand the visitor over to the full page.
        moreBtn.dataset.rvLink = allUrl;
        moreBtn.innerHTML = '<span class="lbl">Read all ' + esc(totalFmt) + ' reviews</span>' + ARROW_R;
      } else {
        moreBtn.hidden = true; // everything there is to show is on screen
      }
    }

    // Nothing is hidden if the cards already fit inside the clamp — drop the fade and the
    // expander rather than veil empty space (a narrow filter leaves only a card or two).
    function settle() {
      if (open || grid.scrollHeight > clampPx() + 8) return;
      open = true;
      reveal.classList.add('open');
      grid.style.maxHeight = 'none';
      setMore();
    }

    // rv-fit hides the permanent veil while an OPEN grid is short enough to fit the
    // clamp: over one or two cards the veil would eat most of what it decorates.
    // Never sticky: content can grow after the flag was set (+STEP renders, index-2
    // arriving mid-filter, Read more expansion, resize), so every such path calls
    // this to re-evaluate rather than trusting an old verdict.
    function fitCheck() {
      reveal.classList.toggle('rv-fit', open && grid.scrollHeight <= clampPx() + 8);
    }

    function render(animateFrom) {
      const vis = capped().slice(0, shown);
      grid.innerHTML = vis.length
        ? vis.map(card).join('')
        : '<div class="rv-empty">No reviews match this filter.</div>';
      if (animateFrom != null && !reduced) {
        Array.prototype.forEach.call(grid.children, (c, i) => {
          if (i >= animateFrom) {
            c.classList.add('enter');
            c.style.animationDelay = ((i - animateFrom) * 65) + 'ms';
          }
        });
      }
      setMore();
      settle();
      fitCheck();
    }

    function grow(mutate) {
      const start = grid.getBoundingClientRect().height;
      grid.style.maxHeight = start + 'px';
      const seq = ++growSeq;
      void grid.offsetHeight;
      if (mutate) mutate();
      grid.style.maxHeight = grid.scrollHeight + 'px';
      let done = false;
      const fin = () => {
        if (done) return;
        done = true;
        grid.removeEventListener('transitionend', onEnd);
        if (seq !== growSeq) return; // superseded by a newer grow or a collapse: not ours to finalize
        grid.style.maxHeight = 'none';
      };
      const onEnd = (e) => { if (e.propertyName === 'max-height') fin(); };
      grid.addEventListener('transitionend', onEnd);
      setTimeout(fin, 1100);
    }

    function collapse() {
      open = false;
      growSeq++; // invalidate any in-flight grow finalizer before re-clamping
      reveal.classList.remove('open', 'rv-fit');
      grid.style.maxHeight = clampPx() + 'px';
    }

    async function refilter() {
      shown = INIT;
      collapse();
      await hydrate();    // never filter an empty pool if a fast interaction beat index-1
      cacheList = null; render();
      await ensureDeep(); // honest counts need the archive, not just the newest 100
      cacheList = null; render();
    }

    // ---- gallery ------------------------------------------------------
    function lbTitle(t) { lightbox().querySelector('[data-lb-title]').textContent = t; }
    const lbBody = () => lightbox().querySelector('[data-lb-body]');

    function renderLbGrid() {
      lbAt = -1;
      lbTitle(photos.length + ' customer photos');
      lbBody().innerHTML = '<div class="rv-lb-grid">' + photos.map((p, i) =>
        '<button type="button" data-lb-i="' + i + '" aria-label="Open photo ' + (i + 1) + '">' +
        '<img src="' + esc(cdn(p.u, 240)) + '" alt="" loading="lazy" decoding="async"></button>'
      ).join('') + '</div>';
    }

    function renderLbOne() {
      const p = photos[lbAt];
      const r = pool[p.k]; // undefined until index-2 lands — caption fills in then
      lbTitle((lbAt + 1) + ' / ' + photos.length);
      lbBody().innerHTML = '<div class="rv-lb-one">' +
        '<img src="' + esc(cdn(p.u, 1400)) + '" alt="Customer photo">' +
        (r ? '<p class="rv-lb-cap"><span class="who">' + esc(r.n || 'Anonymous') + '</span> · ' +
              esc(r.r) + '/5 · ' + esc(ago(r.d)) + '<br>&ldquo;' + esc(r.b || '') + '&rdquo;</p>' : '') +
        '<div class="rv-lb-nav">' +
          '<button type="button" data-lb-prev aria-label="Previous"' + (lbAt <= 0 ? ' disabled' : '') + '>&lsaquo;</button>' +
          '<button type="button" data-lb-all aria-label="All photos">&#9638;</button>' +
          '<button type="button" data-lb-next aria-label="Next"' + (lbAt >= photos.length - 1 ? ' disabled' : '') + '>&rsaquo;</button>' +
        '</div></div>';
    }

    async function openGallery(startK, startUrl) {
      const el = lightbox();
      if (!el.open) el.showModal();
      lbTitle('Loading photos…');
      lbBody().innerHTML = '';
      if (!photos) {
        try { photos = await getJSON(urlPh); }
        catch (e) { lbTitle('Photos unavailable'); return; }
      }
      // Captions need the review behind each photo; don't make the grid wait for it.
      ensureDeep().then(() => { if (lbEl.open && lbAt >= 0) renderLbOne(); });
      let at = -1;
      if (startUrl != null) at = photos.findIndex((p) => p.u === startUrl);
      else if (startK != null) at = photos.findIndex((p) => p.k === startK);
      if (at >= 0) { lbAt = at; renderLbOne(); } else renderLbGrid();
    }

    // ---- events -------------------------------------------------------
    moreBtn.addEventListener('click', () => {
      if (moreBtn.dataset.rvLink) { window.location.href = moreBtn.dataset.rvLink; return; }
      if (!open) {
        open = true;
        reveal.classList.add('open');
        grow();
        setMore();
        return;
      }
      const prev = grid.querySelectorAll('.rv-card').length;
      grow(() => { shown += STEP; render(prev); });
    });

    if (filtersEl) {
      filtersEl.addEventListener('click', (e) => {
        const chip = e.target.closest('.rv-chip');
        if (!chip) return;
        const f = chip.dataset.filter;
        if (active.has(f)) { active.delete(f); chip.setAttribute('aria-pressed', 'false'); }
        else { active.add(f); chip.setAttribute('aria-pressed', 'true'); }
        refilter();
      });
    }

    if (sortEl) {
      sortEl.addEventListener('change', () => { sort = sortEl.value; refilter(); });
    }

    grid.addEventListener('click', (e) => {
      const more = e.target.closest('[data-rv-readmore]');
      if (more) {
        const q = more.previousElementSibling;
        if (q) q.classList.remove('clamp');
        more.remove();
        fitCheck(); // the expanded quote may push a fitting grid past the clamp
        return;
      }
      const shot = e.target.closest('[data-rv-shot]');
      if (shot) openGallery(null, shot.dataset.rvShot);
    });

    const galleryBtn = root.querySelector('[data-rv-gallery]');
    if (galleryBtn) galleryBtn.addEventListener('click', () => openGallery());
    root.querySelectorAll('[data-rv-thumb]').forEach((t) => {
      t.addEventListener('click', () => openGallery(+t.dataset.rvThumb));
    });

    window.addEventListener('resize', () => {
      if (!open) grid.style.maxHeight = clampPx() + 'px';
      fitCheck();
    });

    // ---- histogram: correct without JS, animated with it ---------------
    if (dist && !reduced) {
      const bars = dist.querySelectorAll('.bar i');
      bars.forEach((b) => { b.style.width = '0'; });
      const fill = () => bars.forEach((b) => { b.style.width = ''; });
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((en) => {
          en.forEach((x) => { if (x.isIntersecting) { fill(); io.disconnect(); } });
        }, { threshold: 0.3 });
        io.observe(dist);
      } else fill();
      setTimeout(fill, 1500);
    }

    // ---- go -----------------------------------------------------------
    collapse();

    // Hydrate index-1 only when the section nears the viewport — reviews sit far down the page, so
    // eager loading spends ~150KB many visitors never scroll to; the server-rendered cards hold until
    // then. Archive pages (cap 0) or browsers without IntersectionObserver hydrate immediately.
    function boot() {
      hydrate();
      // The archive page is the one place index-2 is the whole point — pull it once idle.
      if (!cap) {
        var idle = window.requestIdleCallback || function (f) { return setTimeout(f, 400); };
        idle(function () { ensureDeep(); });
      }
    }
    if (!cap || !('IntersectionObserver' in window)) {
      boot();
    } else {
      var io = new IntersectionObserver(function (en) {
        if (en.some(function (x) { return x.isIntersecting; })) { io.disconnect(); boot(); }
      }, { rootMargin: '600px 0px' });
      io.observe(root);
    }
  }

  const boot = () => document.querySelectorAll('[data-rv]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
